import { useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  healthFormsApi,
  QuestionListItem,
  QuestionListParams,
  QuestionnaireListItem,
  QuestionGroupListItem,
  SearchParams,
  UsageItem,
  PublishedQuestionnaireListItem,
  PublishedQuestionnaireDetail,
  PublishCheckConflict,
  PublishedQuestionnaireHistoryItem,
  PatientPreviewResponse,
  QuestionPreviewResponse,
  QuestionGroupPreviewResponse,
  RestorePreview,
  RolloutStatus,
} from "./api";
import type { PreviewAttrs, MedicationSubstance, MedicationBrand } from "./api";
import { Question } from "./types/common";
import { Questionnaire, QuestionGroup, ExpandedQuestionnaire, ExpandedQuestionGroup } from "./types/persisted";

export const queryKeys = {
  questionsBase: ["healthForms", "questions"] as const,
  questions: (params?: QuestionListParams) => ["healthForms", "questions", "list", params] as const,
  question: (id: number) => ["healthForms", "questions", id] as const,
  questionnaires: (params?: SearchParams) => ["healthForms", "questionnaires", "list", params] as const,
  questionnairesBase: ["healthForms", "questionnaires"] as const,
  questionnaire: (id: number) => ["healthForms", "questionnaires", id] as const,
  questionnaireByName: (name: string) => ["healthForms", "questionnaires", "by-name", name] as const,
  questionGroups: (params?: SearchParams) => ["healthForms", "questionGroups", "list", params] as const,
  questionGroupsBase: ["healthForms", "questionGroups"] as const,
  questionGroup: (id: number) => ["healthForms", "questionGroups", id] as const,
  questionUsage: (id: number) => ["healthForms", "questions", id, "usage"] as const,
  questionGroupUsage: (id: number) => ["healthForms", "questionGroups", id, "usage"] as const,
  publishedQuestionnaires: (params?: SearchParams) =>
    ["healthForms", "publishedQuestionnaires", "list", params] as const,
  publishedQuestionnairesBase: ["healthForms", "publishedQuestionnaires"] as const,
  medicationSubstances: ["healthForms", "medicationSubstances"] as const,
  medicationBrands: ["healthForms", "medicationBrands"] as const,
  publishedQuestionnaire: (id: number) => ["healthForms", "publishedQuestionnaires", id] as const,
  questionnairePreview: (id: number, attrs: PreviewAttrs) =>
    ["healthForms", "questionnaires", id, "preview", attrs] as const,
  questionPreview: (id: number, attrs: PreviewAttrs) => ["healthForms", "questions", id, "preview", attrs] as const,
  questionGroupPreview: (id: number, attrs: PreviewAttrs) =>
    ["healthForms", "questionGroups", id, "preview", attrs] as const,
  publishedQuestionnairePreview: (id: number, attrs: PreviewAttrs) =>
    ["healthForms", "publishedQuestionnaires", id, "preview", attrs] as const,
  questionnaireDiffResolved: (id: number, locale: string) =>
    ["healthForms", "questionnaires", id, "diffResolved", locale] as const,
  publishedQuestionnaireDiffResolved: (id: number, locale: string) =>
    ["healthForms", "publishedQuestionnaires", id, "diffResolved", locale] as const,
  publishedQuestionnaireHistory: (name: string) => ["healthForms", "publishedQuestionnaires", "history", name] as const,
  publishCheck: (id: number) => ["healthForms", "publishCheck", id] as const,
  restorePreview: (id: number, publishedId: number) =>
    ["healthForms", "questionnaires", id, "restorePreview", publishedId] as const,
};

// The questionnaire list is the only one carrying derived state (has_unpublished_changes),
// and it rolls up from the questions and groups it contains. So every content mutation
// refreshes its own list plus the questionnaire list.
const useHealthFormMutation = <TData, TVars>(
  ownBase: readonly unknown[],
  mutationFn: (vars: TVars) => Promise<TData>
) => {
  const queryClient = useQueryClient();
  return useMutation(mutationFn, {
    onSuccess: () => {
      queryClient.invalidateQueries(ownBase);
      queryClient.invalidateQueries(queryKeys.questionnairesBase);
    },
  });
};

export const useQuestions = (params?: QuestionListParams) =>
  useQuery<QuestionListItem[]>(queryKeys.questions(params), () => healthFormsApi.questions.list(params));

export const useQuestion = (id: number | null) =>
  useQuery<Question>(queryKeys.question(id ?? 0), () => healthFormsApi.questions.get(id!), {
    enabled: !!id,
  });

export const useUpdateQuestion = () =>
  useHealthFormMutation(queryKeys.questionsBase, ({ id, question }: { id: number; question: Question }) =>
    healthFormsApi.questions.update(id, question)
  );

export const useDeleteQuestion = () =>
  useHealthFormMutation(queryKeys.questionsBase, (id: number) => healthFormsApi.questions.delete(id));

export const useQuestionnaires = (params?: SearchParams) =>
  useQuery<QuestionnaireListItem[]>(queryKeys.questionnaires(params), () => healthFormsApi.questionnaires.list(params));

export const useQuestionnaire = (id: number | null) =>
  useQuery<ExpandedQuestionnaire>(queryKeys.questionnaire(id ?? 0), () => healthFormsApi.questionnaires.get(id!), {
    enabled: !!id,
  });

export const useQuestionnaireByName = (name: string | null) =>
  useQuery<QuestionnaireListItem | null>(
    queryKeys.questionnaireByName(name ?? ""),
    () => healthFormsApi.questionnaires.getByName(name!),
    { enabled: !!name }
  );

export const useUpdateQuestionnaire = () =>
  useHealthFormMutation(
    queryKeys.questionnairesBase,
    ({ id, questionnaire }: { id: number; questionnaire: Questionnaire }) =>
      healthFormsApi.questionnaires.update(id, questionnaire)
  );

export const useDeleteQuestionnaire = () =>
  useHealthFormMutation(queryKeys.questionnairesBase, (id: number) => healthFormsApi.questionnaires.delete(id));

export const useQuestionGroups = (params?: SearchParams) =>
  useQuery<QuestionGroupListItem[]>(queryKeys.questionGroups(params), () => healthFormsApi.questionGroups.list(params));

export const useQuestionGroup = (id: number | null) =>
  useQuery<ExpandedQuestionGroup>(queryKeys.questionGroup(id ?? 0), () => healthFormsApi.questionGroups.get(id!), {
    enabled: !!id,
  });

export const useUpdateQuestionGroup = () =>
  useHealthFormMutation(queryKeys.questionGroupsBase, ({ id, group }: { id: number; group: QuestionGroup }) =>
    healthFormsApi.questionGroups.update(id, group)
  );

export const useDeleteQuestionGroup = () =>
  useHealthFormMutation(queryKeys.questionGroupsBase, (id: number) => healthFormsApi.questionGroups.delete(id));

export const useCreateQuestion = () =>
  useHealthFormMutation(queryKeys.questionsBase, () => healthFormsApi.questions.create());

export const useCreateQuestionnaire = () =>
  useHealthFormMutation(queryKeys.questionnairesBase, (input?: { name?: string; pages?: Questionnaire["pages"] }) =>
    healthFormsApi.questionnaires.create(input)
  );

export const useCreateQuestionGroup = () =>
  useHealthFormMutation(
    queryKeys.questionGroupsBase,
    (input?: Omit<QuestionGroup, "_type" | "id" | "updated_at" | "edited_by">) =>
      healthFormsApi.questionGroups.create(input)
  );

export const useQuestionUsage = (id: number) =>
  useQuery<UsageItem[]>(queryKeys.questionUsage(id), () => healthFormsApi.questions.usage(id));

export const useQuestionGroupUsage = (id: number) =>
  useQuery<UsageItem[]>(queryKeys.questionGroupUsage(id), () => healthFormsApi.questionGroups.usage(id));

const ROLLOUT_POLL_INTERVAL = 30_000;

const isPendingRolloutStatus = (status: RolloutStatus) => status === "activating" || status === "unpublishing";

const pendingRolloutNames = (items?: PublishedQuestionnaireListItem[]) =>
  items?.filter(item => item.rollout.some(entry => isPendingRolloutStatus(entry.status))).map(item => item.name) ?? [];

export const usePublishedQuestionnaires = (params?: SearchParams) => {
  const queryClient = useQueryClient();
  const previousPendingNames = useRef<string[]>([]);
  return useQuery<PublishedQuestionnaireListItem[]>(
    queryKeys.publishedQuestionnaires(params),
    () => healthFormsApi.publishedQuestionnaires.list(params),
    {
      staleTime: 0,
      refetchInterval: data => (pendingRolloutNames(data).length > 0 ? ROLLOUT_POLL_INTERVAL : false),
      onSuccess: data => {
        const pending = pendingRolloutNames(data);
        const affected = new Set([...previousPendingNames.current, ...pending]);
        previousPendingNames.current = pending;
        affected.forEach(name => queryClient.invalidateQueries(queryKeys.publishedQuestionnaireHistory(name)));
      },
    }
  );
};

export const usePublishedQuestionnaire = (id: number | null) =>
  useQuery<PublishedQuestionnaireDetail>(
    queryKeys.publishedQuestionnaire(id ?? 0),
    () => healthFormsApi.publishedQuestionnaires.get(id!),
    { enabled: !!id }
  );

export const useQuestionnairePreview = (id: number | null, attrs: PreviewAttrs) =>
  useQuery<PatientPreviewResponse>(
    queryKeys.questionnairePreview(id ?? 0, attrs),
    () => healthFormsApi.questionnaires.preview(id!, attrs),
    { enabled: !!id }
  );

export const useQuestionPreview = (id: number | null, attrs: PreviewAttrs) =>
  useQuery<QuestionPreviewResponse>(
    queryKeys.questionPreview(id ?? 0, attrs),
    () => healthFormsApi.questions.preview(id!, attrs),
    { enabled: !!id }
  );

export const useQuestionGroupPreview = (id: number | null, attrs: PreviewAttrs) =>
  useQuery<QuestionGroupPreviewResponse>(
    queryKeys.questionGroupPreview(id ?? 0, attrs),
    () => healthFormsApi.questionGroups.preview(id!, attrs),
    { enabled: !!id }
  );

export const usePublishedQuestionnairePreview = (id: number | null, attrs: PreviewAttrs) =>
  useQuery<PatientPreviewResponse>(
    queryKeys.publishedQuestionnairePreview(id ?? 0, attrs),
    () => healthFormsApi.publishedQuestionnaires.preview(id!, attrs),
    { enabled: !!id }
  );

export const usePublishCheck = (id: number | null) =>
  useQuery<PublishCheckConflict | null>(
    queryKeys.publishCheck(id ?? 0),
    () => healthFormsApi.questionnaires.publishCheck(id!),
    { enabled: !!id }
  );

export const usePublishedQuestionnaireHistory = (name: string | null) =>
  useQuery<PublishedQuestionnaireHistoryItem[]>(
    queryKeys.publishedQuestionnaireHistory(name ?? ""),
    () => healthFormsApi.publishedQuestionnaires.history(name!),
    { enabled: !!name }
  );

export const usePublishQuestionnaire = () => {
  const queryClient = useQueryClient();
  return useMutation((id: number) => healthFormsApi.questionnaires.publish(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.publishedQuestionnairesBase);
      queryClient.invalidateQueries(queryKeys.questionnairesBase);
    },
  });
};

export const useRestorePreview = (id: number | null, publishedId: number | null) =>
  useQuery<RestorePreview>(
    queryKeys.restorePreview(id ?? 0, publishedId ?? 0),
    () => healthFormsApi.questionnaires.restorePreview(id!, publishedId!),
    { enabled: !!id && !!publishedId }
  );

// restore rewrites shared questions/groups, so every cached health-forms entity may be stale
export const useRestoreQuestionnaire = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, publishedId }: { id: number; publishedId: number }) =>
      healthFormsApi.questionnaires.restore(id, publishedId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["healthForms"]);
      },
    }
  );
};

export const usePublishToGroup = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ publishedQuestionnaireId, group }: { publishedQuestionnaireId: number; group: string }) =>
      healthFormsApi.rollout.publishToGroup(publishedQuestionnaireId, group),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.publishedQuestionnairesBase);
      },
    }
  );
};

export const useMedicationSubstances = (enabled: boolean) =>
  useQuery<MedicationSubstance[]>(queryKeys.medicationSubstances, () => healthFormsApi.medicationSubstances.list(), {
    staleTime: Infinity,
    enabled,
  });

export const useMedicationBrands = (enabled: boolean) =>
  useQuery<MedicationBrand[]>(queryKeys.medicationBrands, () => healthFormsApi.medicationBrands.list(), {
    staleTime: Infinity,
    enabled,
  });
